import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, InventoryDistributionToggle, useDidUpdate } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { SonataMOREContext } from './SonataMORE.context';
import { SonataMORESettings } from './SonataMORESettings';

import './SonataMORE.styles.scss';

import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { SonataMORESharedContextState } from './SonataMORE.context';
import type { SonataMOREProps } from './SonataMORE.types';

type SonataMOREState = {
  currentCampaignOptimizationModel?: OptimizationModelInterface;
};

/**
 * SonataMORE — Machine Learning Optimization module.
 *
 * Displays the KPI category and KPI selectors plus the budget distribution
 * toggle. Only rendered when the user has the `campaignOptimization` permission.
 * Hidden for DOOH campaigns — guard that with `!isDooh` at the call site.
 */
function SonataMORE({
  campaignCountries,
  campaignOptimizationModel,
  currencyInfo,
  goals,
  setCampaignOptimizationModel,
  user,
  userRoles
}: SonataMOREProps) {
  const { formatMessage } = useIntl();

  const [isEnabled, setIsEnabled] = useState(!isEmpty(campaignOptimizationModel));

  const [{ currentCampaignOptimizationModel }, setLocalState] = useState<SonataMOREState>({
    currentCampaignOptimizationModel: campaignOptimizationModel
  });

  const [sharedState, setSharedState] = useState<SonataMORESharedContextState>({
    currentCampaignOptimizationModel: campaignOptimizationModel,
    models: []
  });

  // Propagate model changes up to the parent store
  useDidUpdate(() => {
    setCampaignOptimizationModel(sharedState.currentCampaignOptimizationModel);
  }, [sharedState.currentCampaignOptimizationModel]);

  // Keep local state in sync with shared context changes
  useDidUpdate(() => {
    setLocalState(prev => ({
      ...prev,
      currentCampaignOptimizationModel: sharedState.currentCampaignOptimizationModel
    }));
  }, [sharedState.currentCampaignOptimizationModel]);

  const handleToggle = (_: string, checked: boolean) => {
    setIsEnabled(checked);
    if (!checked) {
      setSharedState(prev => ({ ...prev, currentCampaignOptimizationModel: undefined }));
    }
  };

  const handlePacingDistributionChange = (newDistribution: { distribution: number; enabled: boolean }) => {
    if (sharedState.currentCampaignOptimizationModel) {
      setSharedState(prevState => ({
        ...prevState,
        currentCampaignOptimizationModel: {
          ...sharedState.currentCampaignOptimizationModel!,
          distribution: {
            ...newDistribution
          }
        }
      }));
    }
  };

  const mloPacingReady =
    !isEmpty(sharedState.currentCampaignOptimizationModel) &&
    checkUserResourcePermissions('campaignOptimizationPacing', user, userRoles) &&
    !isEmpty(currencyInfo);

  let pacingDistribution = 75;
  if (currentCampaignOptimizationModel?.distribution?.distribution) {
    pacingDistribution = currentCampaignOptimizationModel.distribution.distribution * 100;
  }

  // Permission guard — render nothing for unauthorized users
  if (isEmpty(user) || isEmpty(userRoles) || !checkUserResourcePermissions('campaignOptimization', user, userRoles)) {
    return null;
  }

  return (
    <SonataMOREContext.Provider
      value={{
        ...sharedState,
        setContext: setSharedState
      }}
    >
      <div className="sonataMORE">
        <Checkbox
          active={isEnabled}
          classes="sonataMORE__toggle"
          colorSchema="secondary"
          id="sonataMORE-toggle"
          labelText={formatMessage({
            defaultMessage: 'Sonata MORE',
            id: 'sonataMORE.title'
          })}
          onClickAction={handleToggle}
          type="switch"
        />
        {isEnabled && (
          <div className="sonataMORE__body">
            <p className="sonataMORE__subtitle">
              {formatMessage({
                defaultMessage:
                  'Choose the machine learning optimization model that corresponds to your campaign goal and customize the budget distribution.',
                id: 'sonataMORE.subtitle'
              })}
            </p>
            <div className="sonataMORE__formSettings">
              <SonataMORESettings
                campaignCountries={campaignCountries}
                currencyInfo={currencyInfo}
                defaultPacingDistribution={0.75}
                user={user}
                userRoles={userRoles}
              />
              {mloPacingReady && (
                <div className="sonataMORE__distribution">
                  <InventoryDistributionToggle
                    acronymText={formatMessage({
                      defaultMessage: 'OMU',
                      id: 'optimizationModel.inventoryDistribution.acronym'
                    })}
                    currencyInfo={currencyInfo}
                    defaultValue={pacingDistribution}
                    distributionText={formatMessage({
                      defaultMessage: 'Distribution of incoming traffic that will use Optimization Model Usage',
                      id: 'optimizationModel.inventoryDistribution.label'
                    })}
                    enabled={sharedState.currentCampaignOptimizationModel?.distribution?.enabled || false}
                    goals={goals}
                    id="sonataMORE"
                    onChange={distribution =>
                      handlePacingDistributionChange({
                        distribution: distribution.value,
                        enabled: distribution.enabled
                      })
                    }
                    user={user}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SonataMOREContext.Provider>
  );
}

export default SonataMORE;
